import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import Divider from '@mui/material/Divider';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import LockIcon from '@mui/icons-material/Lock';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  useGetApiApprovalQueueQuery,
  useGetApiProjectsDeliverableTypesQuery,
  useLazyGetApiAttachmentsQuery,
  useLazyGetApiDeliverablesQuery,
} from '../../../app/athenaApiGen';
import AuditLog from '../../../components/AuditLog/AuditLog';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { openEditModal } from '../../../app/projects/projectsSlice';
import AthenaStatusChip from '../../../shared-theme/customizations/AthenaStatusChip';

dayjs.extend(utc);
// Reuse the Library's entity icon helper so projects/deliverables read with
// the same visual identity across pages (cyan briefcase for projects,
// neutral inventory box for deliverables).
import { getEntityIcon } from '../../library/components/entityIcon';
import { formViewRouteByAbbreviation } from '../../formSchemaRegistry';
import { isFormTypeAvailable } from '../../utilities';
import ProjectDownloaderOptionsDialog from './ProjectDownloaderOptionsDialog';
import { useProjectPermissions } from '../useProjectPermissions';

// Group-order for deliverables in the View Panel. Mirrors the typical
// reading priority — needs-attention first, in-progress middle, completed
// last. Unknown / Unspecified states fall through and are appended after
// the known set alphabetically.
const DELIVERABLE_STATE_ORDER = [
  // Needs attention
  'Internally Rejected',
  'Federally Rejected',
  'Internally Rejected, In Recovery',
  'Federally Rejected, In Recovery',
  // External review
  'In APM Review',
  'In Theater Director Review',
  'In COR Review',
  'In Federal Review',
  'Waiting on Federal Approval',
  // Internal review
  'In Internal Review',
  'Pending',
  'Waiting on Tyto Approval',
  // Completed
  'Internally Approved',
  'Federally Approved',
  'No Workflow, Approved',
];

// Color for the small dot beside each group header. Kept in lockstep with
// AthenaStatusChip's palette so the visual cue carries across pages.
const deliverableStateColor = (state) => {
  switch (state) {
    case 'Internally Approved':
    case 'Federally Approved':
    case 'No Workflow, Approved':
      return '#6BA539';
    case 'In Internal Review':
    case 'Pending':
    case 'Waiting on Tyto Approval':
      return '#0092C8';
    case 'In APM Review':
    case 'In Theater Director Review':
    case 'In COR Review':
    case 'In Federal Review':
    case 'Waiting on Federal Approval':
      return '#7B1E8E';
    case 'Internally Rejected':
    case 'Federally Rejected':
    case 'Internally Rejected, In Recovery':
    case 'Federally Rejected, In Recovery':
      return '#C51E46';
    default:
      return '#1F4D83';
  }
};

// Date formatting helper — matches the table column format (dayjs UTC,
// MM/DD/YYYY) so the same field reads the same way in both places.
function formatDate(dateStr) {
  if (!dateStr) return '-';
  const d = dayjs.utc(dateStr);
  if (!d.isValid()) return dateStr;
  return d.format('MM/DD/YYYY');
}

export default function ProjectViewPanel({
  project,
  onClose,
  expanded = false,
  onToggleExpanded,
}) {
  const [attachments, setAttachments] = React.useState([]);
  const [isLoadingAttachments, setIsLoadingAttachments] = React.useState(false);
  const [fetchAttachments] = useLazyGetApiAttachmentsQuery();
  const [deliverables, setDeliverables] = React.useState([]);
  const [isLoadingDeliverables, setIsLoadingDeliverables] = React.useState(false);
  const [fetchDeliverables] = useLazyGetApiDeliverablesQuery();

  // Accordion-style expansion state for deliverable groups. Default
  // collapsed so the panel acts as a status summary; user expands the
  // groups they actually care about.
  const [expandedDelGroups, setExpandedDelGroups] = React.useState(
    () => new Set(),
  );
  const toggleDelGroup = (state) => {
    setExpandedDelGroups((prev) => {
      const next = new Set(prev);
      if (next.has(state)) next.delete(state);
      else next.add(state);
      return next;
    });
  };

  // Group deliverables by workflowState and sort groups by the standard
  // priority order so the same project always presents the same layout.
  // Within each group, items are sorted alphabetically by title (case-
  // insensitive, numeric-aware so "Item 2" comes before "Item 10").
  const groupedDeliverables = React.useMemo(() => {
    const groups = new Map();
    for (const d of deliverables) {
      const state = d.workflowState || 'Unspecified';
      if (!groups.has(state)) groups.set(state, []);
      groups.get(state).push(d);
    }
    for (const items of groups.values()) {
      items.sort((a, b) =>
        (a.deliverableTitle || 'Untitled deliverable').localeCompare(
          b.deliverableTitle || 'Untitled deliverable',
          undefined,
          { sensitivity: 'base', numeric: true },
        ),
      );
    }
    return Array.from(groups.entries()).sort(([a], [b]) => {
      const ai = DELIVERABLE_STATE_ORDER.indexOf(a);
      const bi = DELIVERABLE_STATE_ORDER.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
  }, [deliverables]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const availableForms = useSelector((state) => state.user.availableForms);

  // ACL permission check for viewing + editing + downloading this project.
  // canDownloadProject gates the Download Files link below Attachments.
  const { canViewProject, canEditProject, canDownloadProject } =
    useProjectPermissions(
      project?.projectId || project?.id,
      project?.project?.programId,
    );

  // Local state for the panel's own download trigger. The table's row
  // kebab has its own equivalent state — they're independent and only
  // one can be open at a time in practice, so no contention.
  const [downloadProject, setDownloadProject] = React.useState(null);

  // Mirrors the row-kebab Edit handler in ConstProjectColumns.jsx — same
  // shape, same dispatch. Surfacing it on the panel saves the round-trip
  // through the row's three-dot menu.
  const handleEditClick = () => {
    if (!project) return;
    const projectData = {
      title: project.name,
      projectCode: project.code,
      programId: project.project?.programId || '',
      ownerId:
        project.ownerUserId || project.project?.ownerId || '',
      locationId: project.project?.locationId || '',
      description: project.description || '',
      projectFilePath: project.project?.projectFilePath || '',
    };
    dispatch(
      openEditModal({
        projectId: project.projectId,
        projectData,
      })
    );
  };

  const projectId = project?.projectId || project?.id;

  // Deliverable Types assigned to this project — same endpoint the
  // ProjectTypesPanel and Assign-Deliverable-Types wizard step use.
  // Expanded view only; the collapsed side panel skips the extra fetch.
  const { data: projectDeliverableTypesRaw = [] } =
    useGetApiProjectsDeliverableTypesQuery(
      { projectId: projectId ?? '' },
      { skip: !expanded || !projectId || !canViewProject },
    );
  const projectDeliverableTypes = React.useMemo(() => {
    if (!Array.isArray(projectDeliverableTypesRaw)) return [];
    // Drop archived + rows missing the nested type. Sort alphabetically
    // by title so the same project always presents the same order —
    // matches the wizard's "Assign Deliverable Types" step.
    return projectDeliverableTypesRaw
      .filter(
        (t) => t?.deliverableType && !t.archived && !t.deliverableType?.archived,
      )
      .sort((a, b) => {
        const at = (
          a.deliverableType?.title ||
          a.deliverableType?.abbreviation ||
          ''
        ).toLowerCase();
        const bt = (
          b.deliverableType?.title ||
          b.deliverableType?.abbreviation ||
          ''
        ).toLowerCase();
        return at.localeCompare(bt, undefined, {
          sensitivity: 'base',
          numeric: true,
        });
      });
  }, [projectDeliverableTypesRaw]);

  // Audit log — fetched by matching the approval-queue row that carries
  // this project's workflow-task history. Only fired when the panel is
  // actually expanded (the collapsed side view doesn't render the log)
  // to keep the 500-row query out of the compact-mode critical path.
  const { data: queueForAuditLog } = useGetApiApprovalQueueQuery(
    { approvalState: 'all', limit: 500, page: 1 },
    { skip: !expanded || !projectId || !canViewProject },
  );
  const projectAuditLog = React.useMemo(() => {
    if (!queueForAuditLog?.data || !projectId) return [];
    const match = queueForAuditLog.data.find(
      (r) => r.objectType === 'Project' && r.objectId === projectId,
    );
    return match?.auditLog ?? [];
  }, [queueForAuditLog, projectId]);

  // Fetch attachments when project changes. Loading flag drives the
  // "Loading attachments…" placeholder so the empty state doesn't flash
  // before the fetch settles (same bug deliverables had).
  React.useEffect(() => {
    if (project?.id && canViewProject) {
      setIsLoadingAttachments(true);
      setAttachments([]);
      fetchAttachments({
        objectId: project.id,
        objectType: 'Project',
      })
        .unwrap()
        .then((data) => {
          setAttachments(data.attachments || []);
        })
        .catch((error) => {
          console.error('Error fetching attachments:', error);
          setAttachments([]);
        })
        .finally(() => {
          setIsLoadingAttachments(false);
        });
    } else {
      setAttachments([]);
      setIsLoadingAttachments(false);
    }
  }, [project?.id, fetchAttachments, canViewProject]);

  // Fetch deliverables for this project. V1's project page surfaced the
  // deliverables list prominently (with workflow-state badges) — V2 was
  // missing that, leaving the panel feeling like a thin metadata viewer.
  // First page only (limit 50); large projects can be paged in later if
  // needed. Same RTK Query cache as Deliverables page, so dedupes.
  // isLoadingDeliverables drives a "Loading…" placeholder so the empty
  // state doesn't flash misleadingly during the in-flight fetch on
  // projects with many deliverables.
  React.useEffect(() => {
    const projectId = project?.projectId || project?.id;
    if (projectId && canViewProject) {
      setIsLoadingDeliverables(true);
      setDeliverables([]);
      fetchDeliverables({ projectId, page: 1, limit: 50 })
        .unwrap()
        .then((data) => {
          setDeliverables(data.deliverables || []);
        })
        .catch((error) => {
          console.error('Error fetching deliverables:', error);
          setDeliverables([]);
        })
        .finally(() => {
          setIsLoadingDeliverables(false);
        });
    } else {
      setDeliverables([]);
      setIsLoadingDeliverables(false);
    }
  }, [project?.projectId, project?.id, fetchDeliverables, canViewProject]);

  if (!project) return null;

  // Show access denied message if user doesn't have view permission
  if (!canViewProject) {
    return (
      <Box sx={{ p: 3, pb: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="h2">
            Access Denied
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        <Divider sx={{ mb: 3 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 2 }}>
          <LockIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
          <Typography variant="body1" color="text.secondary" align="center">
            You do not have permission to view this project.
          </Typography>
        </Box>
      </Box>
    );
  }

  // In expanded mode, cap the content width so long metadata rows don't
  // stretch to unreadable line lengths on wide monitors. Roughly matches
  // the width of the collapsed side panel × 2.5, which is comfortable
  // for the 2-3 column metadata grids below.
  const contentMaxWidth = expanded ? 1200 : undefined;
  const gridColSize = expanded ? 4 : 6;

  return (
    <Box sx={{ p: 3, pb: 4, height: '100%', display: 'flex', flexDirection: 'column', maxWidth: contentMaxWidth, width: '100%', mx: expanded ? 'auto' : 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
          {getEntityIcon('project')}
          <Typography variant="h6" component="h2" sx={{ minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {project.name || 'Project Details'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
          {onToggleExpanded && (
            <Tooltip title={expanded ? 'Collapse' : 'Expand'} arrow>
              <IconButton
                onClick={onToggleExpanded}
                size="small"
                aria-label={expanded ? 'Collapse panel' : 'Expand panel'}
                sx={{ color: '#0092CB' }}
              >
                {expanded ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Close" arrow>
            <IconButton onClick={onClose} size="small" aria-label="Close panel">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* View Milestones — pinned to the top under the title so the V1-
          mental-model gap (V1 users expect milestones on the project page)
          is bridged immediately, not buried at the bottom of the panel. */}
      <Box sx={{ mt: 1, mb: 1.5 }}>
        <Link
          underline="hover"
          variant="body1"
          onClick={() => navigate('/master-schedule')}
          sx={{
            color: '#0092CB',
            fontWeight: 500,
            cursor: 'pointer',
            textTransform: 'none',
          }}
        >
          View Milestones on Schedule page
        </Link>
      </Box>

      <Divider sx={{ mb: 2.5 }} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, overflow: 'auto', pb: 2, flex: 1, minHeight: 0 }}>
        {/* Top metadata block — Project Code + Status share the top row,
            Description spans full width below. Status reverted to plain
            text (chip was too noisy in this slot); the table column still
            uses the chip where the colored chip earns its keep. */}
        <Grid container rowSpacing={2} columnSpacing={2}>
          <Grid size={gridColSize}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Project Code:
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {project.code || '-'}
            </Typography>
          </Grid>
          <Grid size={gridColSize}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Status:
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {project.workflowState || '-'}
            </Typography>
          </Grid>
          {project.description && (
            <Grid size={12}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Description:
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {project.description}
              </Typography>
            </Grid>
          )}
        </Grid>

        <Divider />

        <Grid container rowSpacing={2} columnSpacing={2}>
          <Grid size={gridColSize}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Owner:
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {project.ownerName || '-'}
            </Typography>
          </Grid>
          <Grid size={gridColSize}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Location:
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {project.location || '-'}
            </Typography>
          </Grid>
          <Grid size={gridColSize}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Creation Date:
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {formatDate(project.creationDate)}
            </Typography>
          </Grid>
          <Grid size={gridColSize}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Last Modified:
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {formatDate(project.lastModified)}
            </Typography>
          </Grid>
        </Grid>

        {/* Deliverable Types assigned to this project (expanded only) —
            same catalog the wizard's "Assign Deliverable Types" step
            configures. Renders as a compact list of chips + titles so
            reviewers can see what types this project will accept
            without opening the ProjectTypesPanel. */}
        {expanded && (
          <>
            <Divider />
            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}
              >
                Deliverable Types ({projectDeliverableTypes.length})
              </Typography>
              {projectDeliverableTypes.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No deliverable types assigned to this project.
                </Typography>
              ) : (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: '1fr 1fr',
                      md: '1fr 1fr 1fr',
                    },
                    columnGap: 2,
                    rowGap: 1,
                  }}
                >
                  {projectDeliverableTypes.map((t) => {
                    const type = t.deliverableType || {};
                    return (
                      <Box
                        key={t.deliverableTypeId || type.deliverableTypeId}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          minWidth: 0,
                        }}
                      >
                        {type.abbreviation && (
                          <Box
                            component="span"
                            sx={{
                              flexShrink: 0,
                              px: 0.75,
                              py: 0.1,
                              borderRadius: 1,
                              backgroundColor: 'action.selected',
                              color: 'text.secondary',
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              letterSpacing: 0.3,
                              textTransform: 'uppercase',
                            }}
                          >
                            {type.abbreviation}
                          </Box>
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={type.title}
                        >
                          {type.title || 'Untitled type'}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          </>
        )}

        <Divider />

        {/* Deliverables — the V1 project page surfaced these prominently on
            the right sidebar with workflow-state badges, which made the page
            feel like a workflow tool rather than a metadata viewer. We
            mirror that here using AthenaStatusChip for state coloring. The
            inventory icon sits with the section header (not per-row) so the
            list scans cleanly. */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
            {getEntityIcon('deliverable')}
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              Deliverables ({deliverables.length})
            </Typography>
          </Box>
          {isLoadingDeliverables ? (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontStyle: 'italic',
                // Animated build-up ellipsis: "" → "." → ".." → "..." → loop.
                // Pseudo-element handles the dots so the React tree doesn't
                // re-render every 400ms. Fixed width on the pseudo-element
                // reserves space so the text after doesn't jitter.
                '&::after': {
                  content: '""',
                  display: 'inline-block',
                  width: '1.25em',
                  textAlign: 'left',
                  animation: 'loadingDots 1.4s linear infinite',
                },
                '@keyframes loadingDots': {
                  '0%, 100%': { content: '""' },
                  '25%': { content: '"."' },
                  '50%': { content: '".."' },
                  '75%': { content: '"..."' },
                },
              }}
            >
              Loading deliverables
            </Typography>
          ) : deliverables.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No deliverables yet
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {groupedDeliverables.map(([state, items]) => {
                const isOpen = expandedDelGroups.has(state);
                return (
                  <Box key={state}>
                    <Box
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleDelGroup(state)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggleDelGroup(state);
                        }
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        cursor: 'pointer',
                        userSelect: 'none',
                        borderRadius: 1,
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                    >
                      {isOpen ? (
                        <ExpandMoreIcon fontSize="small" />
                      ) : (
                        <ChevronRightIcon fontSize="small" />
                      )}
                      <AthenaStatusChip workflowState={state} />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 0.5 }}
                      >
                        ({items.length})
                      </Typography>
                    </Box>
                    {isOpen && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                          pl: 6,
                          mt: 0.5,
                        }}
                      >
                        {items.map((d) => {
                          const abbr = (
                            d.deliverableType?.abbreviation ?? ''
                          ).toLowerCase();
                          // Forms (TAR/TR/PNR/CTP/RIP) have dedicated detail
                          // routes; non-form deliverables deep-link into the
                          // Deliverables page via `?deliverableId=X`, which
                          // selects the row and opens its View Panel. Either
                          // way every row is clickable — V2 feedback was that
                          // dead-text rows felt like the page "did nothing."
                          const isForm = [
                            'ctp',
                            'pnr',
                            'rip',
                            'tar',
                            'tr',
                          ].includes(abbr);
                          const canOpenForm =
                            isForm &&
                            isFormTypeAvailable(availableForms, abbr) &&
                            !!d.deliverableId;
                          const returnTo = project?.projectId
                            ? `/projects?projectId=${project.projectId}`
                            : '/projects';
                          const onOpen = canOpenForm
                            ? () =>
                                navigate(
                                  `/${formViewRouteByAbbreviation[abbr] ?? abbr}/${d.deliverableId}?mode=view`,
                                  { state: { returnTo } },
                                )
                            : () =>
                                navigate(
                                  d.deliverableId
                                    ? `/deliverables?deliverableId=${d.deliverableId}`
                                    : '/deliverables',
                                  { state: { returnTo } },
                                );
                          return (
                            <Box
                              key={d.deliverableId}
                              sx={{
                                display: 'flex',
                                alignItems: 'baseline',
                                gap: 0.75,
                              }}
                            >
                              <Box
                                component="span"
                                aria-hidden
                                sx={{ color: 'text.disabled', lineHeight: 1 }}
                              >
                                •
                              </Box>
                              <Link
                                underline="hover"
                                variant="body2"
                                onClick={onOpen}
                                sx={{
                                  color: '#0092CB',
                                  fontWeight: 500,
                                  cursor: 'pointer',
                                  textTransform: 'none',
                                }}
                              >
                                {d.deliverableType?.abbreviation && (
                                  <Box
                                    component="span"
                                    sx={{
                                      color: 'text.secondary',
                                      mr: 0.75,
                                    }}
                                  >
                                    {d.deliverableType.abbreviation}
                                  </Box>
                                )}
                                {d.deliverableTitle || 'Untitled deliverable'}
                              </Link>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        <Divider />

        <Box>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}
          >
            Attachments ({attachments.length})
          </Typography>
          {isLoadingAttachments ? (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontStyle: 'italic',
                // Matches the animated ellipsis in the deliverables loading
                // state so the two loading lines feel like one pattern.
                '&::after': {
                  content: '""',
                  display: 'inline-block',
                  width: '1.25em',
                  textAlign: 'left',
                  animation: 'loadingDots 1.4s linear infinite',
                },
                '@keyframes loadingDots': {
                  '0%, 100%': { content: '""' },
                  '25%': { content: '"."' },
                  '50%': { content: '".."' },
                  '75%': { content: '"..."' },
                },
              }}
            >
              Loading attachments
            </Typography>
          ) : attachments.length === 0 ? (
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', fontStyle: 'italic' }}
            >
              —
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {attachments.map((attachment) => (
                <Box
                  key={attachment.attachmentId}
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: 1,
                    pl: 0.5 
                  }}
                >
                  <AttachFileIcon fontSize="small" color="action" sx={{ mt: 0.25 }} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {attachment.name || 'Unnamed file'}
                    </Typography>
                    {attachment.description && (
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                        {attachment.description}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* Download Files — sits below the Attachments list with a divider
            separator. Triggers the same ProjectDownloaderOptionsDialog the
            row kebab uses; gated on canDownloadProject so users without
            download-project don't see an affordance they can't use. The
            divider hides too when the link is hidden — no orphan rule. */}
        {canDownloadProject && (
          <>
            <Divider />
            <Box>
              <Link
                underline="hover"
                variant="body1"
                onClick={() => setDownloadProject(project)}
                sx={{
                  color: '#0092CB',
                  fontWeight: 500,
                  cursor: 'pointer',
                  textTransform: 'none',
                }}
              >
                Download Files
              </Link>
            </Box>
          </>
        )}

        {/* Audit log — expanded view only. Same shared AuditLog component
            the approvals ViewPanel and IMCS TAR form use, populated from
            the matching approval-queue row's workflow-task history. */}
        {expanded && (
          <>
            <Divider />
            <Box>
              <AuditLog auditLog={projectAuditLog} />
            </Box>
          </>
        )}
      </Box>

      {/* Sticky footer — Edit Project only now that View Milestones is at
          the top. Footer still hides entirely without edit permission. */}
      {canEditProject && (
        <Box sx={{ flexShrink: 0, mt: 2 }}>
          <Divider sx={{ mb: 1.5 }} />
          <Link
            underline="hover"
            variant="body1"
            onClick={handleEditClick}
            sx={{
              color: '#0092CB',
              fontWeight: 500,
              cursor: 'pointer',
              textTransform: 'none',
              display: 'inline-block',
            }}
          >
            Edit Project
          </Link>
        </Box>
      )}

      {/* Project download dialog — mounted here so the Download Files link
          above can trigger the same flow the table's row-kebab uses. */}
      <ProjectDownloaderOptionsDialog
        open={Boolean(downloadProject)}
        onClose={() => setDownloadProject(null)}
        project={downloadProject}
      />
    </Box>
  );
}

ProjectViewPanel.propTypes = {
  project: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  expanded: PropTypes.bool,
  onToggleExpanded: PropTypes.func,
};
